import { BrandsTable, CatFiveAffinitiesTable } from 'components';
import CategoryScoreCardHero from './CategoryScoreCardHero';
import { DATA_SOURCES } from '@/config/constants';
import DownloadTimeSeriesButton from 'components/Button/DownloadTimeSeriesButton';
import { hasFlavorsTab, isTikTokMarket } from '../shared/entityPredicates';
import EntityBreadcrumbs from '../shared/entity/EntityBreadcrumbs';
import EntityChartDownload from '../shared/entity/EntityChartDownload';
import EntityFlavors from '../shared/entity/EntityFlavors';
import EntityGraphNav from '../shared/entity/EntityGraphNav';
import EntityPage from '../shared/entity/EntityPage';
import EntityTabs, { EntityTab } from '../shared/entity/EntityTabs';
import EntityTimeSeriesTab from '../shared/entity/EntityTimeSeriesTab';
import EntityTopTerms from '../shared/entity/EntityTopTerms';
import GoogleBreadCrumbs from 'components/Breadcrumbs/GoogleBreadCrumbs';
import GoogleEntityChart, {
    googleSeries,
} from '../shared/entity/GoogleEntityChart';
import GoogleGraphNav from 'components/DataVis/GraphNav/GoogleGraphNav';
import { GOOGLE_METADATA_BY_KEY } from 'utils/metrics';
import MarketsSection from 'components/DataVis/MarketsSection';
import Stack from 'components/Stack';
import { SUMMARY_SPEC, scoreCardSummary } from './categorySummary';
import { TopKeywordsSection } from 'components/TopKeywordsSection';
import useGoogleEntity, {
    useGoogleSiblings,
} from '../shared/entity/useGoogleEntity';
import { useCurrentCountry } from 'utils/hooks';
import useParentCategory from './useParentCategory';
import { useTrackDepthView } from 'utils/hooks/useTrackEntityView';
import { views } from '@/copy';

const { categorySummary, categoryThree } = views;
const { affinities, brands, prediction } = categoryThree.sections;

const HERO_METRICS = [
    GOOGLE_METADATA_BY_KEY.YoY,
    GOOGLE_METADATA_BY_KEY.QoQ,
    GOOGLE_METADATA_BY_KEY.MoM,
];

function GoogleCategoryThreePage({ categoryId, controller, tabs }) {
    const country = useCurrentCountry();
    const page = useGoogleEntity({
        entityType: 'categories',
        entityId: categoryId,
        country,
        controller,
        coverage: 'hint',
    });
    const { entity: category, details, row, name, rowQuery } = page;

    const parentCategory = useParentCategory(page);
    const { siblings, ready } = useGoogleSiblings({
        parentCategory,
        country,
        controller,
    });

    useTrackDepthView(DATA_SOURCES.GOOGLE, {
        depth: details?.depth,
        name,
        country,
    });

    const summary = scoreCardSummary({
        entity: category,
        parentCategory,
        siblings,
        ready,
        spec: SUMMARY_SPEC[DATA_SOURCES.GOOGLE],
        currentPage: {
            id: categoryId,
            name,
            volume: row?.volume,
            absoluteGrowth: row?.absoluteGrowth,
            rank: row?.rank || null,
        },
    });

    const series = googleSeries({ page });
    const skipPrediction = isTikTokMarket(page);
    const chartTabName = skipPrediction ? 'Time Series' : 'Prediction';

    return (
        <EntityPage
            page={page}
            breadcrumbs={<EntityBreadcrumbs as={GoogleBreadCrumbs} />}
            sourceTabs={tabs}
            space="32px"
            tabs={
                <EntityTabs>
                    <EntityTab name="Related Searches">
                        <CatFiveAffinitiesTable
                            title={affinities.title}
                            subtitle={affinities.subtitle}
                            tooltipKey="affinitiesTitleHelp"
                            tooltipExample="affinitiesExample"
                            categoryId={categoryId}
                            countryId={country ? country.id : null}
                            controller={controller}
                            name={name}
                            filename={page.exportName('related-search')}
                            sortParamKey="relatedSort"
                        />
                    </EntityTab>
                    <EntityTab name="Brands">
                        <BrandsTable
                            title={brands.title}
                            subtitle={brands.subtitle}
                            tooltipKey="brandsTitleHelp"
                            tooltipExample="brandsExample"
                            countryId={country ? country.id : null}
                            categoryId={categoryId}
                            controller={controller}
                            name={name}
                            filename={page.exportName('brand')}
                            sortParamKey="brandSort"
                        />
                    </EntityTab>
                    {hasFlavorsTab(page) && (
                        <EntityTab name="Flavors">
                            <EntityFlavors
                                dataSource="google"
                                subject="flavorsSearched"
                                exportEntity={name || 'trend'}
                                dated={false}
                            />
                        </EntityTab>
                    )}
                    <EntityTab name={chartTabName}>
                        <EntityTimeSeriesTab
                            title={chartTabName}
                            subtitle={prediction.subtitle}
                            tooltipKey="predictionTitleHelp"
                            refOnWrapper
                            download={
                                <EntityChartDownload
                                    as={DownloadTimeSeriesButton}
                                    data={series}
                                    className="ml-auto"
                                    skipPrediction={skipPrediction}
                                />
                            }
                        >
                            <GoogleEntityChart
                                data={series}
                                skipPrediction={skipPrediction}
                                isLoading={rowQuery.isLoading}
                                placeholderMessage={undefined}
                            />
                        </EntityTimeSeriesTab>
                    </EntityTab>
                    <EntityTab name="Global">
                        <MarketsSection
                            name={name || null}
                            controller={controller}
                            categoryId={categoryId}
                            hierarchy_hint={page.hierarchyHint}
                        />
                    </EntityTab>
                </EntityTabs>
            }
        >
            <Stack $space="16px">
                <CategoryScoreCardHero
                    categoryId={categoryId}
                    name={name}
                    isLoading={rowQuery.isLoading}
                    volumeLabel={categorySummary.scoreCardVolumeText}
                    volume={row?.volume}
                    metrics={row}
                    metricsList={HERO_METRICS}
                    defaultMetric={GOOGLE_METADATA_BY_KEY.YoY}
                    cluster={row?.cluster}
                    {...summary}
                    image={details?.image}
                />
                <EntityGraphNav as={GoogleGraphNav} controller={controller} />
                <EntityTopTerms
                    as={TopKeywordsSection}
                    exportType="search-query"
                    paginateBy="categories"
                    dated={false}
                    controller={controller}
                />
            </Stack>
        </EntityPage>
    );
}

GoogleCategoryThreePage.displayName = 'GoogleCategoryThree';

export default GoogleCategoryThreePage;
